import React, { ChangeEventHandler, FocusEventHandler, ReactNode, useState } from 'react';
import './TextBox.scss';
import { capitalize } from '../Functions';

type guideTextType = 'initial' | 'normal' | 'success' | 'fail' | 'required';
type purposeType = 'uniqueness' | 'verification' | 'send';
export type validationStatusType = 'undone' | 'pending' | 'success' | 'fail';
type buttonStatusType = 'activated' | 'inactivated' | 'pending' | 'success' | 'fail';


export const GuideTextBoxForGeneral = ({text, onChange, onBlur=()=>{}, placeholder, guideTexts, isRequired, isDisabled = false, maxLength = -1, forcedGuideTextType} : {
    text: string,
    onChange: ChangeEventHandler<HTMLInputElement>,
    onBlur?: ChangeEventHandler<HTMLInputElement>,
    placeholder: string,
    guideTexts: {[key in guideTextType]: string},
    isRequired: boolean,
    isDisabled?: boolean,
    maxLength?: number,
    forcedGuideTextType?: guideTextType
}) => {
    const [hasClicked, setHasClicked] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    let guideTextType: guideTextType = 'normal';
    if(isRequired && hasClicked && text.length === 0){
        guideTextType = 'required';
    }
    if(forcedGuideTextType){
        guideTextType = forcedGuideTextType;
    }
    return (
        <TextBox
            text={text}
            placeholder={placeholder}
            guideTextType={guideTextType}
            guideText={guideTexts[guideTextType]}
            maxLength={maxLength}
            isFocused={isFocused}
            isDisabled={isDisabled}
            onChange={onChange}
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={(e) => {
                onBlur(e);
                setHasClicked(true);
                setIsFocused(false);
            }}
        />
    )
}


export const GuideTextBoxForStandAloneVerification = ({purpose, text, onBlur, onChange, placeholder, guideTexts, maxLength = -1, validationStatus, onClick, isDisabled = false, forcedGuideTextType} : {
    purpose: purposeType,
    text: string,
    onBlur?: () => void,
    onChange: ChangeEventHandler<HTMLInputElement>,
    placeholder: string,
    guideTexts: {[key in guideTextType]: string},
    maxLength?: number,
    validationStatus: validationStatusType,
    onClick: Function,
    isDisabled?: boolean,
    forcedGuideTextType?: guideTextType
}) => {
    const [hasClicked, setHasClicked] = useState(false);
    const [isFocused, setIsFocused] = useState(false);            

    let guideTextType: guideTextType = 'normal';
    if(hasClicked && validationStatus === 'undone'){
        guideTextType = 'required';
    } else if(validationStatus === 'success'){
        guideTextType = 'success';
    } else if(validationStatus === 'fail'){
        guideTextType = 'fail';
    }
    if(forcedGuideTextType){
        guideTextType = forcedGuideTextType;
    }

    let buttonStatus: buttonStatusType = 'activated';
    if(text.length === 0){
        buttonStatus = 'inactivated';
    } else if(validationStatus === 'pending'){
        buttonStatus = 'pending';
    } else if(validationStatus === 'success' && !isFocused){
        buttonStatus = 'success';
    } else if(validationStatus === 'fail' && !isFocused){
        buttonStatus = 'fail';
    }

    return (
        <TextBox
            text={text}
            placeholder={placeholder}
            guideTextType={guideTextType}
            guideText={guideTexts[guideTextType]}
            maxLength={maxLength}
            isFocused={isFocused}
            isDisabled={isDisabled || validationStatus === 'pending'}
            onChange={onChange}
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={() => { // for onClick of the button to happen first
                setTimeout(() => {
                    setHasClicked(true);
                    setIsFocused(false);
                }, 50);
                if ( onBlur ) {
                    onBlur();
                }
            }}
        >
            <VerificationButton
                purpose={purpose}
                status={buttonStatus}
                onClick={onClick}
            />
        </TextBox>
    )
}

export const GuideTextBoxForPairedVerification = ({purpose, text, validationPattern, onChange, onBlur, placeholder, guideTexts, maxLength = -1, validationStatus, secondStepValidationStatus, onClick, isDisabled = false, forcedGuideTextType} : {
    purpose: purposeType,
    text: string,
    validationPattern?: RegExp,
    onBlur?: () => void,
    onChange: ChangeEventHandler<HTMLInputElement>,
    placeholder: string,
    guideTexts: {[key in guideTextType]: string},
    maxLength?: number,
    validationStatus: validationStatusType,
    secondStepValidationStatus: validationStatusType,
    onClick: Function,
    isDisabled?: boolean,
    forcedGuideTextType?: guideTextType
}) => {
    const [hasClicked, setHasClicked] = useState(false);
    const [isFocused, setIsFocused] = useState(false);            

    let guideTextType: guideTextType = 'normal';
    if(hasClicked && validationStatus === 'undone' && !(isFocused && text.length > 0)){
        guideTextType = 'required';
    } else if(validationStatus === 'success' && secondStepValidationStatus === 'success' && !isFocused){
        guideTextType = 'success';
    }
    if(forcedGuideTextType){
        guideTextType = forcedGuideTextType;
    }

    let buttonStatus: buttonStatusType = 'activated';
    if(text.length === 0 || (validationStatus === 'success' && secondStepValidationStatus !== 'success') || (validationPattern && !validationPattern.test(text))){
        buttonStatus = 'inactivated';
    } else if(validationStatus === 'success' && secondStepValidationStatus === 'success' && !isFocused){
        buttonStatus = 'success';
    }

    return (
        <TextBox
            text={text}
            placeholder={placeholder}
            guideTextType={guideTextType}
            guideText={guideTexts[guideTextType]}
            maxLength={maxLength}
            isFocused={isFocused}
            isDisabled={isDisabled}
            onChange={onChange}
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={() => { // for onClick of the button to happen first
                setTimeout(() => {
                    setHasClicked(true);
                    setIsFocused(false);
                }, 100);
                if ( onBlur ) {
                    onBlur();
                }
            }}
        >
            <VerificationButton
                purpose={purpose}
                status={buttonStatus}
                onClick={onClick}
            />
        </TextBox>
    )
}


const TextBox = ({children, text, placeholder, guideTextType, guideText, maxLength, onChange, onFocus, onBlur, isFocused, isDisabled} : {
    children?: ReactNode,
    text: string,
    placeholder: string,
    guideTextType: guideTextType,
    guideText: string,
    maxLength: number,
    onChange: ChangeEventHandler<HTMLInputElement>,
    onFocus: FocusEventHandler<HTMLInputElement>,
    onBlur?: FocusEventHandler<HTMLInputElement>,
    isFocused: boolean,
    isDisabled: boolean
}) => {
    let textboxClassList = ['TextBox'];
    if(isDisabled){
        textboxClassList.push('Disabled');
    }
    if(isFocused){
        textboxClassList.push('Focused');
    }
    return (
        <div className={'TextBoxContainer'}>
            <div className={textboxClassList.join(' ')}>
                <input
                    placeholder={placeholder}
                    value={text}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isDisabled}
                />
                {children}
            </div>
            <div className={'TextBoxGuide'}>
                <span className={capitalize(guideTextType)}>{guideText}</span>
                {maxLength >= 0 && <span className={'LetterCount'}>{`${text.length}/${maxLength}`}</span>}
            </div>
        </div>
    );
};



const buttonSrc: {[purpose in purposeType] : {[status in buttonStatusType] : [`http${'' | 's'}://${string}`, ReactNode]}} = {
    uniqueness: {
        activated: ['https://static.webtoon.today/ddah/button/button_primary.png', <span>{'중복확인'}</span>],
        inactivated: ['https://static.webtoon.today/ddah/button/button_primary_light.png', <span>{'중복확인'}</span>],
        pending: ['https://static.webtoon.today/ddah/button/button_primary_light.png', <img className={'Spinner'} src={'https://static.webtoon.today/ddah/icon/icon_spinner.png'} alt={'spinner'}/>],
        success: ['https://static.webtoon.today/ddah/icon/icon_success.svg', <></>],
        fail: ['https://static.webtoon.today/ddah/icon/icon_fail.svg', <></>],
    },
    verification: {
        activated: ['https://static.webtoon.today/ddah/button/button_secondary.png', <span>{'확인'}</span>],
        inactivated: ['https://static.webtoon.today/ddah/button/button_secondary_light.png', <span>{'확인'}</span>],
        pending: ['https://static.webtoon.today/ddah/button/button_secondary_light.png', <img className={'Spinner'} src={'https://static.webtoon.today/ddah/icon/icon_spinner.png'} alt={'spinner'}/>],
        success: ['https://static.webtoon.today/ddah/icon/icon_success.svg', <></>],
        fail: ['https://static.webtoon.today/ddah/icon/icon_fail.svg', <></>],
    },
    send: {
        activated: ['https://static.webtoon.today/ddah/button/button_primary.png', <span>{'인증'}</span>],
        inactivated: ['https://static.webtoon.today/ddah/button/button_primary_light.png', <span>{'인증'}</span>],
        pending: ['https://static.webtoon.today/ddah/button/button_primary_light.png', <img className={'Spinner'} src={'https://static.webtoon.today/ddah/icon/icon_spinner.png'} alt={'spinner'}/>],
        success: ['https://static.webtoon.today/ddah/icon/icon_success.svg', <></>],
        fail: ['https://static.webtoon.today/ddah/icon/icon_fail.svg', <></>],
    },
}
const VerificationButton = ({purpose, status, onClick} : {
    purpose: purposeType,
    status: buttonStatusType,
    onClick: Function
}) => {
    const buttonContent = buttonSrc[purpose][status][1];
    
    return (
        <div
            className={`VerificationButton ${capitalize(status)}`}
            onClick={() => {
                if(status === 'activated'){
                    onClick();
                }
            }}
            tabIndex={0}
        >
            <img
                className={`Background`}
                src={buttonSrc[purpose][status][0]}
                alt={status}
            />
            {buttonContent}
        </div>
    )
}